import React from 'react';
import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { formatMoneyCents } from '../../../theme/chart';
import { personColor, tokens } from '../../../theme/tokens';
import { parseLocalDate } from '../../../utils/bills';
import { useDashboardCategories } from '../../../hooks/useDashboardCategories';
import type { Transaction } from '../../../services/transactionApi';
import FinMark from '../../brand/FinMark';
import { Panel, PanelHeader, PersonDot, Quiet, Tag } from './ui';

/** Entries that came through the bot: text, voice or a photo sent to Fin. */
const FROM_FIN = new Set<Transaction['inputMethod']>(['telegram', 'voice', 'ocr']);

const DAY = 24 * 60 * 60 * 1000;

const useDayLabel = (now: Date) => {
  const { t, i18n } = useTranslation('dashboard');
  return (iso: string) => {
    const date = parseLocalDate(String(iso).slice(0, 10));
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const days = Math.round((today.getTime() - date.getTime()) / DAY);
    if (days === 0) return t('home.today');
    if (days === 1) return t('home.yesterday');
    return date.toLocaleDateString(i18n.language, { day: 'numeric', month: 'short' });
  };
};

const Row: React.FC<{ last: boolean; highlight?: boolean; children: React.ReactNode }> = ({ last, highlight, children }) => (
  <Box
    component='li'
    aria-current={highlight ? 'true' : undefined}
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: { xs: 1.5, md: 1.75 },
      py: 1.25,
      borderBottom: last || highlight ? 'none' : `1px solid ${tokens.line}`,
      // the marked row keeps its content in line with the others
      ...(highlight ? { bgcolor: tokens.accentSoft, borderRadius: '14px', mx: -1.25, px: 1.25 } : {}),
    }}
  >
    {children}
  </Box>
);

const Text: React.FC<{ title: string; sub: React.ReactNode; eyebrow?: string }> = ({ title, sub, eyebrow }) => (
  <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
    {eyebrow && (
      <Box component='span' sx={{ fontSize: 11.5, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: tokens.accent }}>
        {eyebrow}
      </Box>
    )}
    <Box
      component='span'
      // wraps on a phone, where the amount column leaves little room; one line from a tablet up
      sx={{ fontWeight: 600, overflowWrap: 'anywhere', whiteSpace: { sm: 'nowrap' }, overflow: { sm: 'hidden' }, textOverflow: { sm: 'ellipsis' } }}
    >
      {title}
    </Box>
    <Box component='span' sx={{ fontSize: 12.5, color: tokens.ink3, overflowWrap: 'anywhere' }}>{sub}</Box>
  </Box>
);

/** Shown inline on a phone, as a pill beside the row from a tablet up. */
const Flags: React.FC<{ fromFin: boolean; pending: boolean }> = ({ fromFin, pending }) => {
  const { t } = useTranslation('dashboard');
  if (!fromFin && !pending) return null;
  return (
    <Box sx={{ display: { xs: 'none', sm: 'flex' }, gap: 0.75, flexShrink: 0 }}>
      {fromFin && (
        <Tag tone='accent' icon={<FinMark size={13} />}>
          {t('home.recent.byFin')}
        </Tag>
      )}
      {pending && <Tag tone='warn'>{t('home.recent.pending')}</Tag>}
    </Box>
  );
};

const InlineFlags: React.FC<{ fromFin: boolean; pending: boolean }> = ({ fromFin, pending }) => {
  const { t } = useTranslation('dashboard');
  return (
    <Box component='span' sx={{ display: { xs: 'inline', sm: 'none' } }}>
      {fromFin && ` · ${t('home.recent.byFin')}`}
      {pending && (
        <>
          {' · '}
          <Box component='span' sx={{ color: tokens.warn, fontWeight: 700 }}>{t('home.recent.pending')}</Box>
        </>
      )}
    </Box>
  );
};

interface PersonalProps {
  transactions: Transaction[];
  onSeeAll: () => void;
  now?: Date;
}

/** "Últimos lançamentos" of the personal view: what each one cost the user, and how it came in. */
export function PersonalRecent({ transactions, onSeeAll, now = new Date() }: PersonalProps) {
  const { t, i18n } = useTranslation('dashboard');
  const { mapToDisplayCategory } = useDashboardCategories();
  const dayLabel = useDayLabel(now);
  const title = t('home.recent.title');

  return (
    <Panel label={title} sx={{ gap: 1 }}>
      <PanelHeader title={title} action={{ label: t('home.recent.seeAll'), onClick: onSeeAll }} />
      {transactions.length === 0 ? (
        <Quiet>{t('home.recent.empty')}</Quiet>
      ) : (
        <Box component='ul' sx={{ m: 0, p: 0, listStyle: 'none' }}>
          {transactions.map((transaction, index) => {
            const income = transaction.type === 'income';
            const category = transaction.category ? mapToDisplayCategory(transaction.type, transaction.category).name : '';
            const amount = Number(transaction.personalAmount ?? transaction.amount);
            const sign = transaction.type === 'expense' ? '−' : income ? '+' : '';
            const fromFin = FROM_FIN.has(transaction.inputMethod);
            const pending = transaction.status === 'pending';
            const sub = [transaction.merchantName, category, dayLabel(transaction.date)].filter(Boolean).join(' · ');
            return (
              <Row key={transaction.id} last={index === transactions.length - 1}>
                <Box
                  aria-hidden
                  sx={{
                    width: 38,
                    height: 38,
                    flexShrink: 0,
                    borderRadius: '12px',
                    bgcolor: income ? tokens.accentSoft : tokens.bg,
                    color: income ? tokens.accent : tokens.ink2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 12,
                    fontWeight: 700,
                  }}
                >
                  {(category || transaction.description).slice(0, 2).toUpperCase()}
                </Box>
                <Text title={transaction.description} sub={<>{sub}<InlineFlags fromFin={fromFin} pending={pending} /></>} />
                <Flags fromFin={fromFin} pending={pending} />
                <Box
                  component='strong'
                  sx={{ minWidth: { md: 96 }, textAlign: 'right', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums', color: income ? tokens.accent : tokens.ink }}
                >
                  {sign}
                  {formatMoneyCents(amount, transaction.currency, i18n.language)}
                </Box>
              </Row>
            );
          })}
        </Box>
      )}
    </Panel>
  );
}

export interface GroupMember {
  userId: string;
  firstName: string;
}

interface GroupProps {
  transactions: Transaction[];
  members: GroupMember[];
  currentUserId?: string;
  onSeeAll: () => void;
  /** an entry to mark: the one a link from Fin came to show */
  highlightId?: string;
  now?: Date;
}

/**
 * "Últimos lançamentos do grupo": who paid, how it was split, and what it
 * means for the user — their part, what they advanced, or nothing at all.
 */
export function GroupRecent({ transactions, members, currentUserId, onSeeAll, highlightId, now = new Date() }: GroupProps) {
  const { t, i18n } = useTranslation('dashboard');
  const dayLabel = useDayLabel(now);
  const title = t('group.recent.title');
  const money = (value: number, currency: string) => formatMoneyCents(value, currency, i18n.language);
  const indexOf = (userId: string) => Math.max(members.findIndex((member) => member.userId === userId), 0);
  const nameOf = (userId: string, fallback?: string) =>
    userId === currentUserId ? t('balances.you') : members.find((member) => member.userId === userId)?.firstName ?? fallback ?? '?';
  const joinNames = (names: string[]) =>
    names.length <= 1 ? names.join('') : `${names.slice(0, -1).join(', ')} ${t('group.and')} ${names[names.length - 1]}`;

  const splitText = (transaction: Transaction, payerId: string): string => {
    const shares = transaction.shares ?? [];
    const ids = shares.map((share) => share.userId);
    if (ids.length === 0 || (ids.length === 1 && ids[0] === payerId)) return t('group.recent.notSplit');
    if (!ids.includes(payerId)) {
      return t('group.recent.forOthers', { names: joinNames(ids.map((id) => nameOf(id, shares.find((s) => s.userId === id)?.user?.firstName))) });
    }
    if (ids.length >= members.length) return t('group.recent.splitAll', { count: ids.length });
    const others = ids.filter((id) => id !== payerId).map((id) => nameOf(id, shares.find((s) => s.userId === id)?.user?.firstName));
    return t('group.recent.splitWith', { names: joinNames(others) });
  };

  const effect = (transaction: Transaction, payerId: string): { text: string; tone: 'accent' | 'muted' } | null => {
    if (transaction.type !== 'expense' || !currentUserId) return null;
    const amount = Number(transaction.amount);
    const shares = transaction.shares ?? [];
    const mine = shares.length ? Number(shares.find((share) => share.userId === currentUserId)?.amount ?? 0) : payerId === currentUserId ? amount : 0;
    if (payerId === currentUserId) {
      const advanced = Math.round((amount - mine) * 100) / 100;
      return advanced > 0 ? { text: t('group.recent.advanced', { amount: money(advanced, transaction.currency) }), tone: 'accent' } : null;
    }
    return mine > 0
      ? { text: t('group.recent.yourPart', { amount: money(mine, transaction.currency) }), tone: 'muted' }
      : { text: t('group.recent.notYou'), tone: 'muted' };
  };

  return (
    <Panel label={title} sx={{ gap: 1 }}>
      <PanelHeader title={title} action={{ label: t('home.recent.seeAll'), onClick: onSeeAll }} />
      {transactions.length === 0 ? (
        <Quiet>{t('group.recent.empty')}</Quiet>
      ) : (
        <Box component='ul' sx={{ m: 0, p: 0, listStyle: 'none' }}>
          {transactions.map((transaction, index) => {
            const payerId = transaction.paidByUserId ?? transaction.userId;
            const payerName = nameOf(payerId, transaction.paidBy?.firstName ?? transaction.user?.firstName);
            const person = personColor(indexOf(payerId));
            const result = effect(transaction, payerId);
            const fromFin = FROM_FIN.has(transaction.inputMethod);
            const pending = transaction.status === 'pending';
            const paid = payerId === currentUserId ? t('group.recent.youPaid') : t('group.recent.paid', { name: payerName });
            const sub = transaction.type === 'expense'
              ? [paid, splitText(transaction, payerId), dayLabel(transaction.date)].join(' · ')
              : dayLabel(transaction.date);
            return (
              <Row key={transaction.id} last={index === transactions.length - 1} highlight={transaction.id === highlightId}>
                <PersonDot name={payerName} color={person.color} tint={person.tint} size={38} />
                <Text
                  title={transaction.description}
                  sub={<>{sub}<InlineFlags fromFin={fromFin} pending={pending} /></>}
                  eyebrow={transaction.id === highlightId ? t('group.recent.yours') : undefined}
                />
                <Flags fromFin={fromFin} pending={pending} />
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', flexShrink: 0 }}>
                  <Box component='strong' sx={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                    {money(Number(transaction.amount), transaction.currency)}
                  </Box>
                  {result && (
                    <Box
                      component='span'
                      sx={{ fontSize: 12.5, whiteSpace: 'nowrap', color: result.tone === 'accent' ? tokens.accent : tokens.ink3, fontWeight: result.tone === 'accent' ? 600 : 400 }}
                    >
                      {result.text}
                    </Box>
                  )}
                </Box>
              </Row>
            );
          })}
        </Box>
      )}
    </Panel>
  );
}
